// Small local "x min ago" helper — no date-fns dependency for a single call site.
//
// workspace-members cycle (THI-110 Task 5) — first caller with a FUTURE timestamp (a pending
// invite's `expiresAt`, `Members.tsx`). Every prior caller only ever passed a past one
// (createdAt/lastCrawledAt/...), so `diffMs` was always >= 0 and the `< 30_000` branch below
// only ever meant "just now". A future date makes `diffMs` negative, which used to satisfy
// that same `< 30_000` check for ANY distance in the future and always print "just now" —
// wrong for e.g. an invite that expires in 6 days. Branches on sign first, using `absMs` for
// every threshold below, so a past call site's output is byte-identical to before (same
// numbers, same rounding), and a future one gets its own "in Xh" phrasing instead.
//
// `t: Catalog['time']` is a REQUIRED parameter (locale-dashboard plan, Task 3) — no default —
// so the compiler is what proves every call site was updated; there is deliberately no runtime
// fallback to hard-coded English. Branch structure and thresholds are unchanged from before
// extraction; only the strings moved.
import type { Catalog } from '../locales/en';

export function relativeTime(input: string | Date | null | undefined, t: Catalog['time']): string {
  if (!input) return t.never;
  const date = typeof input === 'string' ? new Date(input) : input;
  const diffMs = Date.now() - date.getTime();
  if (Number.isNaN(diffMs)) return t.never;
  const future = diffMs < 0;
  const absMs = Math.abs(diffMs);
  if (absMs < 30_000) return future ? t.inAMoment : t.justNow;

  const absSec = Math.round(absMs / 1000);
  if (absSec < 60) return future ? t.inSeconds({ n: absSec }) : t.secondsAgo({ n: absSec });

  const absMin = Math.round(absSec / 60);
  if (absMin < 60) return future ? t.inMinutes({ n: absMin }) : t.minutesAgo({ n: absMin });

  const absHour = Math.round(absMin / 60);
  if (absHour < 24) return future ? t.inHours({ n: absHour }) : t.hoursAgo({ n: absHour });

  const absDay = Math.round(absHour / 24);
  if (absDay < 30) return future ? t.inDays({ n: absDay }) : t.daysAgo({ n: absDay });

  const absMonth = Math.round(absDay / 30);
  if (absMonth < 12) return future ? t.inMonths({ n: absMonth }) : t.monthsAgo({ n: absMonth });

  const absYear = Math.round(absMonth / 12);
  return future ? t.inYears({ n: absYear }) : t.yearsAgo({ n: absYear });
}
